/**
 * Scenarios – DriverForm and DriversList
 *
 * DriverForm:
 *  - Lets the user pick the driver type from the template's allowed list.
 *  - Shows type-specific fields from DRIVER_CONFIGS.
 *  - Contextual notices for:
 *      headcount  → engine preserves/pro-rates active months (not even distribution)
 *      new_investment → no baseline amounts assumed; everything explicit
 *      commitment → source references must be immutable snapshots; required for source-backed
 */

import { useState } from "react";
import { AlertCircle, Clock, Layers, Loader2, Lock } from "lucide-react";
import type {
  Scenario,
  ScenarioDriverInput,
  ScenarioDriverType,
} from "@workspace/api-client-react";
import { formatMoney } from "@/components/tekledger-ui";
import {
  LICENSE_MODEL_LABELS,
  summarizeTiers,
  summarizeTrueUp,
} from "@/components/license-economics/types";
import {
  DRIVER_CONFIGS,
  ACTOR,
  defaultDriverForm,
  type DriverFormState,
  type ScenarioScope,
} from "./types";
import {
  DRIVER_TYPE_LABELS,
  formatTs,
  friendlyDriverName,
  isMachineName,
} from "./utils";
import { LicensingDriverForm } from "./licensing-driver-form";

// ─── DriverForm ────────────────────────────────────────────────────────────────

export function DriverForm({
  allowedTypes,
  questionKey,
  scope,
  onSubmit,
  onCancel,
  isPending,
  error,
}: {
  allowedTypes: ScenarioDriverType[];
  questionKey: string;
  scope: ScenarioScope;
  onSubmit: (driver: ScenarioDriverInput) => void;
  onCancel: () => void;
  isPending: boolean;
  error?: string;
}) {
  const [form, setForm] = useState<DriverFormState>(
    defaultDriverForm(allowedTypes[0])
  );

  const config = DRIVER_CONFIGS[form.type];
  const isLicensing = form.type === "licensing_model_change";

  const setField = (key: keyof DriverFormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleTypeChange = (type: ScenarioDriverType) => {
    setForm(defaultDriverForm(type));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const refs = form.sourceReferences
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const driver: ScenarioDriverInput = {
      questionKey,
      type: form.type,
      name: form.name.trim(),
      actor: ACTOR,
      ...(form.glAccount ? { glAccount: form.glAccount.trim() } : {}),
      ...(form.startPeriod ? { startPeriod: Number(form.startPeriod) } : {}),
      ...(form.endPeriod ? { endPeriod: Number(form.endPeriod) } : {}),
      ...(form.fromPeriod ? { fromPeriod: Number(form.fromPeriod) } : {}),
      ...(form.toPeriod ? { toPeriod: Number(form.toPeriod) } : {}),
      ...(form.targetFiscalYear ? { targetFiscalYear: Number(form.targetFiscalYear) } : {}),
      ...(form.amount ? { amount: Number(form.amount) } : {}),
      ...(form.percentage ? { percentage: Number(form.percentage) } : {}),
      ...(form.quantityDelta ? { quantityDelta: Number(form.quantityDelta) } : {}),
      ...(form.unitRate ? { unitRate: Number(form.unitRate) } : {}),
      ...(form.annualSalary ? { annualSalary: Number(form.annualSalary) } : {}),
      ...(form.benefitRate ? { benefitRate: Number(form.benefitRate) } : {}),
      ...(refs.length > 0 ? { sourceReferences: refs } : {}),
    };

    onSubmit(driver);
  };

  return (
    <form className="sc-driver-form" onSubmit={handleSubmit} data-testid="driver-form">
      {/* Type selection */}
      <div className="sc-driver-form__head">
        <span className="sc-label">Driver type</span>
        <div className="sc-driver-type-pills" data-testid="driver-type-pills">
          {allowedTypes.map((t) => (
            <button
              type="button"
              key={t}
              className={`sc-pill ${form.type === t ? "sc-pill--active" : ""}`}
              onClick={() => handleTypeChange(t)}
              data-testid={`driver-type-${t}`}
            >
              {DRIVER_TYPE_LABELS[t]}
            </button>
          ))}
        </div>

        {isLicensing && (
          <div className="sc-driver-form__desc">
            <p>
              Test a licensing model change against a reusable License Economics
              input. Select a record, then set the scenario model, effective
              timing, and any seat / usage / growth / rate / fixed / minimum
              commitment overrides.
            </p>
          </div>
        )}

        {!isLicensing && config && (
          <div className="sc-driver-form__desc">
            <p>{config.description}</p>

            {/* Headcount: engine preserves/pro-rates active months — not guaranteed even */}
            {form.type === "headcount" && (
              <div className="sc-phasing-note" data-testid="phasing-note">
                <Clock size={11} />
                The engine preserves already-active months and pro-rates cost
                over the start–end range. To control month-by-month amounts
                precisely, supply monthly overrides via the API.
              </div>
            )}

            {/* New investment: approval ref required; total spread evenly across months if no monthly overrides */}
            {form.type === "new_investment" && (
              <div className="sc-investment-note" data-testid="investment-note">
                <AlertCircle size={11} />
                An approval or source reference is required. No baseline amounts
                are read — if monthly allocations are omitted, the fixed total
                is spread evenly across the selected months.
              </div>
            )}

            {/* Commitment: source references must be immutable snapshots */}
            {form.type === "commitment" && (
              <div className="sc-source-required-note" data-testid="source-required-note">
                <AlertCircle size={11} />
                Source references are required for source-backed commitment
                drivers and must point to immutable snapshots (contract ID,
                PO reference). The engine rejects source-backed commitments
                without them.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fields */}
      <div className="sc-driver-form__fields">
        {/* Driver name — always first */}
        <div className="sc-field">
          <label htmlFor="driver-name" className="sc-label">
            Driver name <span className="sc-required">*</span>
          </label>
          <input
            id="driver-name"
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder={`e.g. ${DRIVER_TYPE_LABELS[form.type]} — describe the change`}
            required
            data-testid="input-driver-name"
          />
          <span className="sc-field__hint">
            Use a plain, descriptive name. Avoid codes — a clear name keeps the
            scenario readable during a review.
          </span>
        </div>

        {!isLicensing &&
          config?.fields.map((field) => (
            <div className="sc-field" key={field.key}>
              <label htmlFor={`driver-${field.key}`} className="sc-label">
                {field.label}
                {field.required && <span className="sc-required"> *</span>}
              </label>
              <input
                id={`driver-${field.key}`}
                type={field.type ?? "text"}
                value={form[field.key]}
                onChange={(e) => setField(field.key, e.target.value)}
                placeholder={field.hint ?? ""}
                required={field.required}
                data-testid={`input-driver-${field.key}`}
                step={field.type === "number" ? "any" : undefined}
              />
              {field.hint && (
                <span className="sc-field__hint">{field.hint}</span>
              )}
            </div>
          ))}
      </div>

      {/* Licensing model change: dedicated sub-form (own actions) */}
      {isLicensing && (
        <LicensingDriverForm
          name={form.name}
          scope={scope}
          onSubmit={onSubmit}
          onCancel={onCancel}
          isPending={isPending}
          error={error}
        />
      )}

      {/* Inline error (non-licensing forms) */}
      {!isLicensing && error && (
        <div className="sc-form-error" role="alert" data-testid="driver-form-error">
          <AlertCircle size={13} />
          <span>{error}</span>
        </div>
      )}

      {/* Actions (non-licensing forms) */}
      {!isLicensing && (
        <div className="sc-driver-form__actions">
          <button
            type="button"
            className="tl-btn"
            onClick={onCancel}
            data-testid="button-cancel-driver"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="tl-btn primary"
            disabled={isPending || !form.name.trim()}
            data-testid="button-submit-driver"
          >
            {isPending ? (
              <>
                <Loader2 size={12} className="animate-spin" /> Saving driver…
              </>
            ) : (
              <>Add driver</>
            )}
          </button>
        </div>
      )}
    </form>
  );
}
// ─── DriversList ───────────────────────────────────────────────────────────────

export function DriversList({ scenario }: { scenario: Scenario }) {
  if (!scenario.drivers || scenario.drivers.length === 0) {
    return (
      <div className="sc-empty-state" data-testid="drivers-empty">
        <Layers size={18} />
        <p>No drivers added yet. Add a driver to model this scenario's assumptions.</p>
      </div>
    );
  }

  return (
    <div className="sc-drivers-list" data-testid="drivers-list">
      {scenario.drivers.map((driver) => (
        <div
          className="sc-driver-card"
          key={driver.driverId}
          data-testid={`driver-card-${driver.driverId}`}
        >
          <div className="sc-driver-card__head">
            <div>
              <span className="sc-driver-type-tag">
                {DRIVER_TYPE_LABELS[driver.type]}
              </span>
              <strong className="sc-driver-card__name">
                {friendlyDriverName(driver)}
              </strong>
              {isMachineName(driver.name) && (
                <span className="sc-driver-card__rawname">
                  Reference: <code>{driver.name}</code>
                </span>
              )}
            </div>
            <span className="sc-driver-card__date">
              {formatTs(driver.createdAt)}
            </span>
          </div>
          <div className="sc-driver-card__meta">
            {driver.glAccount && (
              <span>
                GL: <code>{driver.glAccount}</code>
              </span>
            )}
            {driver.fromPeriod != null && (
              <span>From: P{driver.fromPeriod}</span>
            )}
            {driver.toPeriod != null && (
              <span>To: P{driver.toPeriod}</span>
            )}
            {driver.targetFiscalYear != null && (
              <span>Target FY: {driver.targetFiscalYear}</span>
            )}
            {driver.startPeriod != null && driver.fromPeriod == null && (
              <span>Start: P{driver.startPeriod}</span>
            )}
            {driver.endPeriod != null && driver.toPeriod == null && (
              <span>End: P{driver.endPeriod}</span>
            )}
            {driver.amount != null && (
              <span>Amount: {formatMoney(driver.amount)}</span>
            )}
            {driver.percentage != null && (
              <span>Rate: {driver.percentage}%</span>
            )}
            {driver.quantityDelta != null && (
              <span>
                Qty delta:{" "}
                {driver.quantityDelta > 0 ? "+" : ""}
                {driver.quantityDelta}
              </span>
            )}
            {driver.unitRate != null && (
              <span>Unit rate: {formatMoney(driver.unitRate)}</span>
            )}
            {driver.annualSalary != null && (
              <span>Salary: {formatMoney(driver.annualSalary)}/yr</span>
            )}
            {driver.benefitRate != null && (
              <span>Benefit rate: {driver.benefitRate}%</span>
            )}
            {driver.sourceReferences && driver.sourceReferences.length > 0 && (
              <span>Sources: {driver.sourceReferences.join(", ")}</span>
            )}
          </div>
          {driver.type === "licensing_model_change" && driver.licenseSnapshot && (
            <div
              className="sc-license-snapshot"
              data-testid={`license-snapshot-${driver.driverId}`}
            >
              <div className="sc-license-snapshot__head">
                <strong>Captured contract terms</strong>
                <span><Lock size={10} /> Immutable</span>
              </div>
              <p>
                {driver.licenseSnapshot.vendor} · {driver.licenseSnapshot.productWorkload} ·{" "}
                {LICENSE_MODEL_LABELS[driver.licenseSnapshot.currentModel]} →{" "}
                {LICENSE_MODEL_LABELS[
                  driver.scenarioModel ?? driver.licenseSnapshot.futureModel
                ]}
              </p>
              <div>
                <span>
                  Seat: {summarizeTiers(
                    driver.licenseSnapshot.seatTiers,
                    driver.licenseSnapshot.seatRate,
                    "seat"
                  )}
                </span>
                <span>
                  Usage: {summarizeTiers(
                    driver.licenseSnapshot.usageTiers,
                    driver.licenseSnapshot.unitRate,
                    driver.licenseSnapshot.usageUnit || "unit"
                  )}
                </span>
                <span>True-up: {summarizeTrueUp(driver.licenseSnapshot)}</span>
              </div>
            </div>
          )}
          {driver.actor && (
            <div className="sc-driver-card__actor">by {driver.actor}</div>
          )}
        </div>
      ))}
    </div>
  );
}
